import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import api from '../../api';

const TYPE_LABELS = {
  post_visit_thankyou: 'Thank You',
  new_client_welcome: 'Welcome',
  lapsed_45: 'Win-Back',
};
const TYPE_COLORS = {
  post_visit_thankyou: '#10b981',
  new_client_welcome: '#2B7FFF',
  lapsed_45: '#f59e0b',
};

function daysSince(dateStr) {
  if (!dateStr) return '—';
  const d = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  return `${d}d ago`;
}

export default function CRM() {
  const [stats, setStats] = useState(null);
  const [atRisk, setAtRisk] = useState([]);
  const [lapsed, setLapsed] = useState([]);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/crm/stats'),
      api.get('/crm/at-risk'),
      api.get('/crm/lapsed'),
      api.get('/crm/history'),
    ]).then(([s, ar, l, h]) => {
      setStats(s.data);
      setAtRisk(ar.data);
      setLapsed(l.data);
      setHistory(h.data);
    }).finally(() => setLoading(false));
  }, []);

  const TABS = ['overview', 'at-risk', 'lapsed', 'history'];

  return (
    <AdminLayout>
      <div style={{ marginBottom: '32px' }}>
        <div style={{ color: '#2B7FFF', fontSize: '11px', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '6px' }}>Retention</div>
        <h1 className="page-title">CRM</h1>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '4px' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            flex: 1, padding: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer',
            background: tab === t ? 'rgba(43,127,255,0.15)' : 'transparent',
            color: tab === t ? '#2B7FFF' : '#555',
            fontSize: '12px', fontWeight: 700, textTransform: 'capitalize', letterSpacing: '0.04em',
          }}>{t.replace('-', ' ')}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : (
        <>
          {/* OVERVIEW */}
          {tab === 'overview' && stats && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                {[
                  { label: 'At Risk', value: stats.atRisk, color: '#f59e0b', sub: '21–44 days since last visit' },
                  { label: 'Lapsed', value: stats.lapsed, color: '#ef4444', sub: '45+ days gone' },
                  { label: 'Total Sent', value: stats.totalSent, color: '#2B7FFF', sub: 'All-time follow-ups' },
                  { label: 'This Week', value: stats.sentThisWeek, color: '#10b981', sub: 'Sent last 7 days' },
                ].map(s => (
                  <div key={s.label} className="card" style={{ textAlign: 'center', padding: '20px' }}>
                    <div style={{ fontSize: '32px', fontWeight: 900, color: s.color, letterSpacing: '-0.03em', lineHeight: 1 }}>{s.value}</div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: '6px' }}>{s.label}</div>
                    <div style={{ fontSize: '11px', color: '#444', marginTop: '4px' }}>{s.sub}</div>
                  </div>
                ))}
              </div>

              <div className="card">
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.10em', marginBottom: '16px' }}>Active Sequences</div>
                {[
                  { label: 'Post-Visit Thank You', timing: '24h after completed appointment', status: 'Live', color: '#10b981' },
                  { label: 'New Client Welcome', timing: '24h after first-ever completed appointment', status: 'Live', color: '#10b981' },
                  { label: '21-Day Rebook Nudge', timing: '21 days since last visit, no future booking', status: 'Live', color: '#10b981' },
                  { label: '45-Day Win-Back', timing: '45+ days lapsed, one-time send', status: 'Live', color: '#10b981' },
                  { label: 'Weekly Rebooking Check', timing: 'Every Monday — all 4-week inactive clients', status: 'Live', color: '#10b981' },
                ].map((seq, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: i < 4 ? '1px solid rgba(255,255,255,0.04)' : 'none', gap: '12px' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '13px' }}>{seq.label}</div>
                      <div style={{ fontSize: '11px', color: '#445', marginTop: '3px' }}>{seq.timing}</div>
                    </div>
                    <span style={{ fontSize: '10px', fontWeight: 800, padding: '3px 10px', borderRadius: '4px', background: `${seq.color}18`, color: seq.color, border: `1px solid ${seq.color}33`, whiteSpace: 'nowrap' }}>{seq.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AT RISK */}
          {tab === 'at-risk' && (
            <div className="card">
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.10em', marginBottom: '16px' }}>
                At-Risk Clients — {atRisk.length} clients · 21–44 days since last visit
              </div>
              {atRisk.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#555', padding: '40px', fontSize: '13px' }}>No at-risk clients right now — all clear</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {atRisk.map(c => (
                    <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderRadius: '8px', background: 'rgba(245,158,11,0.04)', border: '1px solid rgba(245,158,11,0.10)' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '13px' }}>{c.name}</div>
                        <div style={{ fontSize: '11px', color: '#555', marginTop: '2px' }}>{c.email || c.phone || '—'} · {c.total_visits} visits</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#f59e0b' }}>{c.days_since}d ago</div>
                        <div style={{ fontSize: '11px', color: '#445' }}>Last: {c.last_visit ? new Date(c.last_visit).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* LAPSED */}
          {tab === 'lapsed' && (
            <div className="card">
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.10em', marginBottom: '16px' }}>
                Lapsed Clients — {lapsed.length} clients · 45+ days gone
              </div>
              {lapsed.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#555', padding: '40px', fontSize: '13px' }}>No lapsed clients. Keep it up!</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {lapsed.map(c => (
                    <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderRadius: '8px', background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.10)' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '13px' }}>{c.name}</div>
                        <div style={{ fontSize: '11px', color: '#555', marginTop: '2px' }}>{c.email || c.phone || '—'} · {c.total_visits} visits</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#ef4444' }}>{c.days_since}d ago</div>
                        <div style={{ fontSize: '11px', color: '#445' }}>Last: {c.last_visit ? new Date(c.last_visit).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* HISTORY */}
          {tab === 'history' && (
            <div className="card">
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.10em', marginBottom: '16px' }}>Recent Follow-Ups Sent</div>
              {history.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#555', padding: '40px', fontSize: '13px' }}>No follow-ups sent yet — sequences run daily at 10 AM.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {history.map(h => {
                    const typeKey = Object.keys(TYPE_LABELS).find(k => h.type.startsWith(k)) || h.type;
                    const color = TYPE_COLORS[typeKey] || '#555';
                    const label = TYPE_LABELS[typeKey] || h.type;
                    return (
                      <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: '8px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '13px' }}>{h.client_name || '—'}</div>
                          <div style={{ fontSize: '11px', color: '#445', marginTop: '2px' }}>{h.email}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: '10px', fontWeight: 800, padding: '2px 8px', borderRadius: '4px', background: `${color}18`, color, border: `1px solid ${color}33` }}>{label}</span>
                          <div style={{ fontSize: '11px', color: '#445', marginTop: '4px' }}>{daysSince(h.sent_at)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}
